import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon,
  Percent,
  Loader2,
  Save,
  MessageSquare,
  Smartphone,
  TestTube,
  AlertCircle,
  CheckCircle,
  Globe,
  Phone,
  Mail,
  MapPin,
} from 'lucide-react';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  
  const [commissionSettings, setCommissionSettings] = useState({
    retailer_commission: 2.0,
    distributor_commission: 1.0,
  });
  
  const [apiSettings, setApiSettings] = useState({
    whatsapp_api_key: '',
    whatsapp_sender: '',
    recharge_api_token: '',
    recharge_api_base_url: 'http://tripathytelecom.com/RechargeApi',
    use_real_api: false,
    callback_url: '',
  });
  
  const [testPhone, setTestPhone] = useState('');

  const [siteSettings, setSiteSettings] = useState({
    site_company_name: 'TRIPATHY TELECOM',
    site_phone: '+91 9999999999',
    site_email: 'info@tripathytelecom.shop',
    site_address: 'West Bengal, India',
    site_tagline: 'Bharat-er sobcheye bhorsajogyo recharge platform',
    site_about: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [commRes, apiRes, siteRes] = await Promise.all([
        settingsAPI.getCommissionSettings(),
        settingsAPI.getApiSettings(),
        settingsAPI.getSiteSettings(),
      ]);
      setCommissionSettings(commRes.data);
      setApiSettings(apiRes.data);
      setSiteSettings(prev => ({ ...prev, ...siteRes.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCommission = async () => {
    if (commissionSettings.retailer_commission < 0 || commissionSettings.distributor_commission < 0) {
      toast.error('Commission rates cannot be negative');
      return;
    }

    setSaving(true);
    try {
      await settingsAPI.updateCommissionSettings(commissionSettings);
      toast.success('Commission settings saved successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApiSettings = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateApiSettings(apiSettings);
      toast.success('API settings saved successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSiteSettings = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateSiteSettings(siteSettings);
      toast.success('Site settings saved successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save site settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };


  const handleTestWhatsApp = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number to test');
      return;
    }

    setTestingWhatsApp(true);
    try {
      const response = await settingsAPI.testWhatsApp(testPhone);
      if (response.data.status) {
        toast.success('Test message sent successfully!');
      } else {
        toast.error(response.data.msg || 'Failed to send test message');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to test WhatsApp';
      toast.error(message);
    } finally {
      setTestingWhatsApp(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-5 animate-fade-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-muted-foreground">Manage system configuration and integrations</p>
        </div>

        <Tabs defaultValue="site">
          <TabsList className="grid w-full grid-cols-1">
            <TabsTrigger value="site" data-testid="tab-site">
              <Globe className="w-4 h-4 mr-2" />
              Site Info
            </TabsTrigger>
          </TabsList>

          {/* Site Settings */}
          <TabsContent value="site" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#1e1b4b15' }}>
                    <Globe className="w-5 h-5" style={{ color: '#4680ff' }} />
                  </div>
                  <div>
                    <CardTitle>Site Information</CardTitle>
                    <CardDescription>Manage your homepage footer and contact details</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="site_company_name">Company Name</Label>
                      <Input
                        id="site_company_name"
                        placeholder="TRIPATHY TELECOM"
                        value={siteSettings.site_company_name}
                        onChange={(e) => setSiteSettings({ ...siteSettings, site_company_name: e.target.value })}
                        data-testid="site-company-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="site_phone">Contact Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="site_phone"
                          placeholder="+91 9999999999"
                          value={siteSettings.site_phone}
                          onChange={(e) => setSiteSettings({ ...siteSettings, site_phone: e.target.value })}
                          className="pl-10"
                          data-testid="site-phone-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="site_email">Contact Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="site_email"
                          placeholder="info@tripathytelecom.shop"
                          value={siteSettings.site_email}
                          onChange={(e) => setSiteSettings({ ...siteSettings, site_email: e.target.value })}
                          className="pl-10"
                          data-testid="site-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="site_address">Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="site_address"
                          placeholder="West Bengal, India"
                          value={siteSettings.site_address}
                          onChange={(e) => setSiteSettings({ ...siteSettings, site_address: e.target.value })}
                          className="pl-10"
                          data-testid="site-address-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="site_tagline">Tagline</Label>
                      <Input
                        id="site_tagline"
                        placeholder="Your tagline here..."
                        value={siteSettings.site_tagline}
                        onChange={(e) => setSiteSettings({ ...siteSettings, site_tagline: e.target.value })}
                        data-testid="site-tagline-input"
                      />
                      <p className="text-sm text-muted-foreground">Shown on the homepage hero and footer</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="site_about">About Text</Label>
                      <textarea
                        id="site_about"
                        rows={4}
                        placeholder="Tell visitors about your business..."
                        value={siteSettings.site_about}
                        onChange={(e) => setSiteSettings({ ...siteSettings, site_about: e.target.value })}
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid="site-about-input"
                      />
                      <p className="text-sm text-muted-foreground">Shown in the About section on the homepage</p>
                    </div>

                    <Button
                      onClick={handleSaveSiteSettings}
                      disabled={saving}
                      className="bg-[#4680ff] hover:bg-[#2563eb]"
                      className="text-white"
                      data-testid="save-site-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 spinner" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Site Settings
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
